import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  Alert
} from '@mui/material';
import { RootState, AppDispatch } from '../store/store';
import { updateProjectAsync } from '../store/projectSlice';
import styles from './Prompts.module.css';

interface PromptsProps {
  className?: string;
}

const Prompts: React.FC<PromptsProps> = ({ className }) => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector((state: RootState) => state.projects.selectedProject);
  const loading = useSelector((state: RootState) => state.projects.loading);
  
  const [basePrompt, setBasePrompt] = useState('');
  const [introMessage, setIntroMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Initialize form values when project changes
  useEffect(() => {
    if (project) {
      setBasePrompt(project.base_prompt || '');
      setIntroMessage(project.intro_message || '');
    }
  }, [project]);

  const handleEdit = () => {
    setIsEditing(true);
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form values to original project values
    if (project) {
      setBasePrompt(project.base_prompt || '');
      setIntroMessage(project.intro_message || '');
    }
    // Clear any messages
    setError(null);
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    if (!project?.id) return;

    try {
      setError(null);
      
      // Create project update object with only the fields we're changing
      const projectUpdate = {
        base_prompt: basePrompt,
        intro_message: introMessage
      };

      // Dispatch update action
      await dispatch(updateProjectAsync({ 
        id: project.id, 
        project: projectUpdate 
      }));
      
      setIsEditing(false);
      setSuccessMessage('Prompts updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to update prompts:', error);
      setError('Failed to update prompts. Please try again.');
    }
  };

  if (!project) return null;

  return (
    <Box className={`${styles.container} ${className || ''}`}>
      <Typography variant="h5" className={styles.moduleTitle}>
        Prompts
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {isEditing ? (
        <>
          <TextField
            label="System Prompt"
            fullWidth
            multiline
            minRows={4}
            value={basePrompt}
            onChange={(e) => setBasePrompt(e.target.value)}
            margin="normal"
            className={`${styles.textField} ${styles.systemPromptField}`}
          />
          
          <TextField
            label="Intro Message"
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={introMessage}
            onChange={(e) => setIntroMessage(e.target.value)}
            margin="normal"
            className={styles.textField}
          />
          
          <Box className={styles.buttonContainer}>
            <Button 
              onClick={handleCancel} 
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={loading}
              variant="contained" 
              color="primary"
              sx={{ ml: 1 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box className={`${styles.promptBox} ${styles.systemPrompt}`}>
            <Typography variant="subtitle1" gutterBottom>
              System Prompt
            </Typography>
            <Box className={styles.promptContent}>
              {project.base_prompt || <em>No system prompt defined</em>}
            </Box>
          </Box>
          
          <Box className={styles.promptBox}>
            <Typography variant="subtitle1" gutterBottom>
              Intro Message
            </Typography>
            <Box className={styles.promptContent}>
              {project.intro_message || <em>No intro message defined</em>}
            </Box>
          </Box>
          
          <Box className={styles.buttonContainer}>
            <Button 
              onClick={handleEdit}
              disabled={loading}
              variant="outlined"
              color="primary"
            >
              Edit Prompts
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Prompts;
